import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRightIcon, CalendarIcon, MapPinIcon, XIcon, ZoomInIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { COMMUNITY_EVENTS, CommunityEvent } from '../data/community-events';
const EASE = [0.23, 1, 0.32, 1] as const;
function EventLink({
  url,
  label,
  className
}: {
  url: string;
  label: string;
  className: string;
}) {
  const isExternal = url.startsWith('http');
  return <a href={url} target={isExternal ? '_blank' : undefined} rel={isExternal ? 'noopener noreferrer' : undefined} className={className} data-id="element-3542" data-mp="2cm1pexhkp1qb">
      {label}
      <ArrowUpRightIcon className="w-3.5 h-3.5" data-id="element-3543" data-mp="2loaydix70n1l" />
    </a>;
}
function EventCard({
  event,
  isEs,
  onZoom
}: {
  event: CommunityEvent;
  isEs: boolean;
  onZoom: (event: CommunityEvent) => void;
}) {
  const title = isEs && event.titleEs ? event.titleEs : event.title;
  const date = isEs && event.dateEs ? event.dateEs : event.date;
  return <div className="flex-shrink-0 w-64 mx-3 flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden transition-shadow duration-200 hover:shadow-lg" data-id="element-3544" data-mp="1eu5kb7ykd9vd">
      {/* Every card opens its flyer full-size */}
      <button type="button" onClick={() => onZoom(event)} className="text-left" aria-label={`${isEs ? 'Ver el flyer de' : 'View the flyer for'} ${title}`} data-id="element-3545" data-mp="103ew09w6ksx0">
        <div className="relative w-full h-40 overflow-hidden bg-haa-navy/5" data-id="element-3546" data-mp="1ss3pbmqzvty7">
          <img src={event.image} alt={title} loading="lazy" className="w-full h-full object-cover object-top" data-id="element-3547" data-mp="1spzml84u10zi" />
          {event.past ? <span className="absolute top-2 left-2 rounded-full bg-gray-800/90 px-2.5 py-1 font-nunito text-[10px] font-bold uppercase tracking-[0.14em] text-white" data-id="element-3548" data-mp="1iw0meeobjra7">
              {isEs ? 'Evento pasado' : 'Past Event'}
            </span> : null}
          <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-haa-navy/85 px-2.5 py-1 font-nunito text-[10px] font-bold uppercase tracking-[0.14em] text-white" data-id="element-3549" data-mp="2vlrca3hzv8yv">
            <ZoomInIcon className="w-3 h-3" data-id="element-3550" data-mp="2llgx4dcefrh2" />
            {isEs ? 'Ver flyer' : 'View flyer'}
          </span>
        </div>
        <div className="px-4 pt-3.5 pb-3" data-id="element-3551" data-mp="2qjihr2ph3yun">
          <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.18em] text-haa-blue mb-1.5 truncate" data-id="element-3552" data-mp="2bvxdv6krxvlo">
            {event.organization}
          </p>
          <h3 className="font-bebas text-lg tracking-[0.03em] text-haa-navy leading-tight mb-2 line-clamp-2" data-id="element-3553" data-mp="2gpa2hgpge2ef">
            {title}
          </h3>
          <p className="flex items-center gap-1.5 font-nunito text-xs font-bold text-haa-navy/70 mb-1" data-id="element-3554" data-mp="27u2ew8qbfub2">
            <CalendarIcon className="w-3 h-3 flex-shrink-0 text-haa-blue" data-id="element-3555" data-mp="1sxbf3ycqcgkx" />
            {date}
          </p>
          <p className="flex items-center gap-1.5 font-nunito text-xs text-haa-navy/55" data-id="element-3556" data-mp="1xvmm81bn4czg">
            <MapPinIcon className="w-3 h-3 flex-shrink-0 text-red-600" data-id="element-3557" data-mp="1ea4ccfxeeox4" />
            {event.city}
          </p>
        </div>
      </button>

      {/* Events with a registration or info page get their own action */}
      {event.url ? <div className="mt-auto px-4 pb-4" data-id="element-3558" data-mp="1oadaw0ev4mfo">
          <EventLink url={event.url} label={isEs ? 'Más información' : 'Event details'} className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-haa-blue px-4 py-2.5 font-nunito text-[11px] font-bold uppercase tracking-[0.14em] text-white transition-colors duration-200 hover:brightness-110" data-id="element-3559" data-mp="1j9fu9s9i40ga" />
        </div> : null}
    </div>;
}

/**
 * Continuously scrolling band of upcoming community events.
 * Every card opens its flyer full-size; events with a registration or
 * info page also carry their own link button.
 */
export function UpcomingEventsScroller() {
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const [zoomed, setZoomed] = useState<CommunityEvent | null>(null);
  useEffect(() => {
    if (!zoomed) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setZoomed(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomed]);
  if (COMMUNITY_EVENTS.length === 0) return null;
  const doubled = [...COMMUNITY_EVENTS, ...COMMUNITY_EVENTS];
  const zoomedTitle = zoomed ? isEs && zoomed.titleEs ? zoomed.titleEs : zoomed.title : '';
  return <section className="bg-white border-y border-gray-200 py-8 overflow-hidden" aria-label={isEs ? 'Próximos eventos' : 'Upcoming events'} data-id="element-3560" data-mp="3rjf8h8z7ct2m">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-5" data-id="element-3561" data-mp="5b5mlmohz7hh">
        <span className="font-nunito text-haa-blue text-xs uppercase tracking-[0.3em] font-semibold block mb-1" data-id="element-3562" data-mp="1bqzhi768pavn">
          {isEs ? 'Calendario Comunitario' : 'Community Calendar'}
        </span>
        <h2 className="font-bebas text-2xl sm:text-3xl tracking-[0.03em] text-haa-navy leading-tight" data-id="element-3563" data-mp="317bsb93888kf">
          {isEs ? 'Próximos Eventos' : 'Upcoming Events'}
        </h2>
      </div>

      <div className="group relative w-full overflow-hidden" data-id="element-3564" data-mp="2rhsam6r92kr5">
        <div className="flex w-max items-stretch animate-events-marquee group-hover:[animation-play-state:paused]" data-id="element-3565" data-mp="2rfxowq85mom0">
          {doubled.map((event, i) => <EventCard key={`${event.title}-${i}`} event={event} isEs={isEs} onZoom={setZoomed} data-id="element-3566" data-mp="2hk464p3udyvs" data-mpi={i} />)}
        </div>
      </div>

      <AnimatePresence data-id="element-3567" data-mp="28i4mdp1g0gp5">
        {zoomed ? <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.18,
        ease: EASE
      }} className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-4 bg-black/85 p-4 sm:p-8" onClick={() => setZoomed(null)} role="dialog" aria-modal="true" aria-label={zoomedTitle} data-id="element-3568" data-mp="28l0j5a1k0zc0">
            <motion.img initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.96
        }} transition={{
          duration: 0.22,
          ease: EASE
        }} src={zoomed.image} alt={zoomedTitle} onClick={e => e.stopPropagation()} className="min-h-0 max-h-full max-w-full sm:max-w-3xl object-contain rounded-lg shadow-2xl" data-id="element-3569" data-mp="20zn7126vy7o9" />
            {zoomed.url ? <div onClick={e => e.stopPropagation()} data-id="element-3570" data-mp="251rxxt5y8q77">
                <EventLink url={zoomed.url} label={isEs ? 'Más información' : 'Event details'} className="inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] text-haa-navy transition-colors duration-200 hover:bg-white/90" data-id="element-3571" data-mp="253ct9l038d64" />
              </div> : null}
            <button type="button" onClick={() => setZoomed(null)} className="absolute top-4 right-4 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/15 text-white transition-colors duration-150 hover:bg-white/30" aria-label={isEs ? 'Cerrar' : 'Close'} data-id="element-3572" data-mp="3cuif0zbe4r12">
              <XIcon className="w-5 h-5" data-id="element-3573" data-mp="2u395wr5j84a0" />
            </button>
          </motion.div> : null}
      </AnimatePresence>
    </section>;
}